import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaAngleLeft } from 'react-icons/fa6';
import { validateVirtualCardWithdrawal, initiateVirtualCardWithdrawal, getVirtualCardDetails, getVirtualCardProducts } from '../../../../api/api';
import { fetchExchangeRates } from '../../../../redux/slices/countriesSlice';

// Bootstrap modal dependency
import { Modal } from 'bootstrap';
import CardOperationSummary from '../CardOperationSummary';

const CardWithdraw = ({ userDetails }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { cardId } = useParams();
    const [amount, setAmount] = useState("");
    const [isEdited, setIsEdited] = useState(false);
    const [loading, setLoading] = useState(false);
    const [initiating, setInitiating] = useState(false);
    const [validationData, setValidationData] = useState(null);
    const [validatedPayload, setValidatedPayload] = useState(null);
    const [card, setCard] = useState(null);
    const [products, setProducts] = useState([]);

    const { conversionRates } = useSelector((state) => state.country);

    useEffect(() => {
        const loadCardAndProducts = async () => {
            try {
                const [cardResponse, productsResponse] = await Promise.all([
                    getVirtualCardDetails(cardId),
                    getVirtualCardProducts(),
                ]);
                setCard(cardResponse.data?.data || null);
                setProducts(productsResponse.data?.data?.virtual_card_products || []);
            } catch (error) {
                toast.error("Unable to load the card balance");
            }
        };
        if (cardId) loadCardAndProducts();
    }, [cardId]);

    useEffect(() => {
        dispatch(fetchExchangeRates());
    }, [dispatch]);
    
    const handleAmountChange = (e) => {
        setAmount(e.target.value);
        setIsEdited(true);
    };

    const handleSubmit = async () => {
        if (!amount || parseFloat(amount) <= 0) return;
        setLoading(true);
        try {
            const payload = {
                withdraw_amount: parseFloat(amount),
            };
            const response = await validateVirtualCardWithdrawal(cardId, payload);
            setValidationData(response.data?.data || {});
            setValidatedPayload(payload);
            
            // Show modal programmatically
            const modal = new Modal(document.getElementById('withdrawModal'));
            modal.show();
        } catch (error) {
            const responseData = error.response?.data;
            const fieldError = responseData?.errors
                ? Object.values(responseData.errors).flat().find(Boolean)
                : null;
            toast.error(fieldError || responseData?.message || "Validation failed!", {
                toastId: "withdraw-fail-toast",
                className: "custom-toast",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmWithdrawal = async () => {
        setInitiating(true);
        try {
            if (!validatedPayload) return;
            await initiateVirtualCardWithdrawal(cardId, validatedPayload);
            
            const modalEl = document.getElementById('withdrawModal');
            const modal = Modal.getInstance(modalEl);
            if (modal) modal.hide();

            toast.success("Withdrawal successful!", {
                toastId: "withdraw-success-toast",
                className: "custom-toast",
            });
            
            setAmount("");
            navigate(`/user/cards/${cardId}`);
        } catch (error) {
            toast.error(error.response?.data?.message || "Withdrawal failed!", {
                toastId: "withdraw-fail-toast",
                className: "custom-toast",
            });
        } finally {
            setInitiating(false);
        }
    };

    const handleGoBack = () => {
        navigate(`/user/cards/${cardId}`);
    };

    const balance = parseFloat(card?.virtual_card_account?.balance ?? card?.balance ?? 0);
    const cardCurrency = card?.currency || card?.card_currency || "USD";
    const walletCurrency = userDetails?.default_wallet?.currency_code || 'NGN';
    const walletBalance = Number(
        String(userDetails?.default_wallet?.balance || 0).replace(/[^0-9.-]+/g, "")
    ) || 0;

    const getExchangeRate = () => {
        if (cardCurrency === walletCurrency) return 1;
        const walletRateData = (conversionRates || []).find(r => r.currency_code === walletCurrency);
        return walletRateData ? parseFloat(walletRateData.buy_rate) : 0;
    };

    const usdToNgnRate = getExchangeRate();

    const calculateWithdrawalFee = () => {
        if (!products.length || !amount) return 0;

        let matchingProduct = products[0];
        if (card) {
            matchingProduct = products.find(p =>
                p.currency?.toLowerCase() === (card.currency || card.card?.currency || 'usd').toLowerCase()
            ) || products[0];
        }

        if (!matchingProduct || !matchingProduct.fee_configurations) return 0;

        const withdrawalActionNames = ['withdraw', 'withdrawal', 'debit'];
        let withdrawConfigs = matchingProduct.fee_configurations.filter(
            f => withdrawalActionNames.includes(f.card_action?.toLowerCase())
        );
        if (!withdrawConfigs.length) {
            // Fallback to fund config if backend only defines balance change fees once
            withdrawConfigs = matchingProduct.fee_configurations.filter(
                f => f.card_action?.toLowerCase() === 'fund'
            );
        }
        if (!withdrawConfigs.length) return 0;

        const amt = parseFloat(amount);
        let applicableConfig = withdrawConfigs.find(f => {
            const min = f.minimum_amount ? parseFloat(f.minimum_amount) : 0;
            const max = f.maximum_amount ? parseFloat(f.maximum_amount) : Infinity;
            return amt >= min && amt <= max;
        });

        if (!applicableConfig) {
            applicableConfig = withdrawConfigs[0];
        }

        if (applicableConfig.fee_type === 'flat') {
            return parseFloat(applicableConfig.flat_fee_amount || 0);
        } else if (applicableConfig.fee_type === 'percentage') {
            return (amt * parseFloat(applicableConfig.fee_percentage || 0)) / 100;
        } else if (applicableConfig.fee_type === 'hybrid') {
            return parseFloat(applicableConfig.flat_fee_amount || 0) + ((amt * parseFloat(applicableConfig.fee_percentage || 0)) / 100);
        }
        return 0;
    };

    const withdrawalFee = calculateWithdrawalFee();
    const netWithdrawalAmountUsd = Math.max(0, (parseFloat(amount) || 0) - withdrawalFee);
    const netWithdrawalAmount = netWithdrawalAmountUsd * usdToNgnRate;

    const formatWalletCurrency = (value) => new Intl.NumberFormat(
        walletCurrency === "NGN" ? "en-NG" : "en-US", {
        style: "currency",
        currency: walletCurrency,
        minimumFractionDigits: 2,
    }).format(value);

    const formatCardCurrency = (value) => new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: cardCurrency,
        currencyDisplay: "code",
        minimumFractionDigits: 2,
    }).format(value);

    const formattedWithdrawalAmount = `${cardCurrency} ${parseFloat(validatedPayload?.withdraw_amount || amount || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

    return (
        <section className='create__card__page card__fund__page'>
            <section className="create-card_section funding__card__section">
                <div className="container py-4 bank-fund-page">
                    <div className="bank-fund-heading">
                        <button onClick={handleGoBack} className="bank-fund-back-icon" aria-label="Back to card options">
                            <FaAngleLeft size={15} />
                        </button>
                        <h2>Withdraw from Card</h2>
                    </div>

                    <div className="bank-fund-content">

                        <div className="bank-fund-form">
                            <div className="bank-fund-field">
                                <label htmlFor="withdraw-amount">Amount to withdraw</label>
                                <div className="bank-fund-input-wrap">
                                    <span>{cardCurrency}</span>
                                    <input id="withdraw-amount" type="number" placeholder="0.00" value={amount} onChange={handleAmountChange} min="0.01" step="0.01" />
                                </div>
                                <div className="bank-fund-quick-amounts">
                                    {[25, 50, 100].map((quickAmount) => (
                                        <button key={quickAmount} type="button" onClick={() => { setAmount(String(quickAmount)); setIsEdited(true); }}>
                                            {cardCurrency} {quickAmount}
                                        </button>
                                    ))}
                                    <button type="button" disabled={balance <= 0} onClick={() => { setAmount(String(balance)); setIsEdited(true); }}>Max</button>
                                </div>
                            </div>
                            <div className="bank-fund-summary">
                                <div><span>Available Balance</span><strong>{formatCardCurrency(balance)}</strong></div>
                                <div><span>Withdrawal Fee</span><strong>{formatCardCurrency(withdrawalFee)}</strong></div>
                                <div><span>Exchange Rate</span><strong>{usdToNgnRate ? `1 ${cardCurrency} = ${formatWalletCurrency(usdToNgnRate)}` : "Loading rate..."}</strong></div>
                                <div className="bank-fund-total"><span>You Will Receive</span><strong>{formatWalletCurrency(netWithdrawalAmount)}</strong></div>
                            </div>

                            <button onClick={handleSubmit} className="bank-fund-submit" disabled={!isEdited || !amount || parseFloat(amount) <= 0 || parseFloat(amount) > balance || loading}>
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Validating...
                                    </>
                                ) : "Proceed"}
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bootstrap Modal */}
            <section className="modal fade" id="withdrawModal" tabIndex="-1" aria-labelledby="withdrawModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
                        <div className="d-flex justify-content-end p-3 pb-0">
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body mb-4 mx-4 text-center">
                            <CardOperationSummary
                                operationType="withdraw"
                                title=""
                                data={{
                                    ...validationData,
                                    card_event_amount: formatWalletCurrency(netWithdrawalAmount),
                                    wallet_impact: {
                                        display_title: "Your Wallet",
                                        display_subtitle: `Balance: ${formatWalletCurrency(walletBalance)}`,
                                    },
                                    withdraw_amount: formatCardCurrency(validatedPayload?.withdraw_amount || 0),
                                }}
                                primaryAmount={{
                                    value: formatCardCurrency(validatedPayload?.withdraw_amount || 0),
                                    label: "Withdrawal amount",
                                }}
                                hiddenKeys={["currency", "balance", "available_balance"]}
                                hiddenGroupKeys={["account"]}
                            />

                            <div className="d-flex gap-3 mt-4">
                                <button className="btn btn-outline-secondary w-50 rounded-pill py-2.5 fw-semibold" data-bs-dismiss="modal" aria-label="Close" disabled={initiating} style={{ fontSize: '14px' }}>
                                    Cancel
                                </button>
                                <button className="btn primary__btn w-50 rounded-pill py-2.5 fw-semibold" onClick={handleConfirmWithdrawal} disabled={initiating} style={{ fontSize: '14px' }}>
                                    {initiating ? "Processing..." : "Confirm Withdrawal"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </section>
    );
};

export default CardWithdraw;
